import { Helmet } from "@dr.pogodin/react-helmet";
import { Outlet } from "react-router";
import styles from "./AmbulatingContainer.module.scss";
import clsx from "clsx";
import { InlineAlert } from "@medoma/components/InlineAlert/InlineAlert";
import { applicationSchema, useMatrixClient } from "@medoma/chat/matrixClient";
import { useCallback, useEffect, useState } from "react";
import { ClientEvent } from "matrix-js-sdk";
import ChatContext from "@medoma/chat/ChatContext";
import { useUnreadNotifications } from "@medoma/chat/useUnreadNotifications";
import { usePushyRegistration } from "@/Utils/PushyUtils";
import { getEmployeeJwt } from "@/api/Chat";
import { useIntercomToken } from "@medoma/support/useIntercomToken";
import { logisticsApi } from "@/api/ApiClient";
import {
  useGoLogisticsSSEHandlers,
  useGoPatientSSEHandlers,
} from "@/Utils/sseHandlers";
import { LoadingOverlay } from "@medoma/components/Loading/Loading";
import Header from "@/pages/ambulating/Header";
import { useAppVisibilityBreadcrumbs } from "../useAppVisibilityBreadcrumbs";
import { userManager, INTERCOM_APP_ID, ENVIRONMENT } from "@/Utils/EnvUtils";
import { z } from "zod";
import { notificationApi } from "@/api/ApiClient";
import { useUserProfile } from "@/api/Users";
import { IntercomProvider as BaseIntercomProvider } from "@medoma/support/IntercomProvider";
import type { ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import * as Sentry from "@sentry/react";

// https://developer.mozilla.org/en-US/docs/Web/API/Notification/permission_static#browser_compatibility
// Not supported on iOS Safari < 16.4, iOS Safari not on Home Screen, or WebViews
const isNotificationPermissionGranted = () => {
  if ("Notification" in window) {
    return Notification.permission === "granted";
  }
  return false;
};

const useNotificationPermissionGranted = () => {
  const [notificationPermissionGranted, setNotificationPermissionGranted] =
    useState(isNotificationPermissionGranted);

  navigator.permissions
    .query({ name: "notifications" })
    .then((permissionStatus) => {
      permissionStatus.onchange = () => {
        setNotificationPermissionGranted(permissionStatus.state === "granted");
      };
    })
    .catch((error: unknown) => Sentry.captureException(error));
  return notificationPermissionGranted;
};

const NotificationPermitter = () => {
  const { t } = useLingui();
  const { employeeNumber } = useUserProfile();
  const pushyUserId = employeeNumber;
  const { registerDeviceWithPushyAndSyncPushyWithNotificationApi } =
    usePushyRegistration();

  const notificationPermissionGranted = useNotificationPermissionGranted();

  // Do a background "refresh", ensuring that the correct user is associated with the device
  useEffect(() => {
    if (notificationPermissionGranted) {
      registerDeviceWithPushyAndSyncPushyWithNotificationApi(pushyUserId);
    }
  }, [
    notificationPermissionGranted,
    pushyUserId,
    registerDeviceWithPushyAndSyncPushyWithNotificationApi,
  ]);

  if (notificationPermissionGranted) {
    return null;
  }

  return (
    <InlineAlert
      title={t`Tillåt aviseringar`}
      message={t`Din tillåtelse för aviseringar behövs för att du ska få notiser om nya meddelanden i chatten`}
      type="warning"
      actions={[
        {
          label: t`Tillåt`,
          onClick: () =>
            registerDeviceWithPushyAndSyncPushyWithNotificationApi(pushyUserId),
          isPrimary: true,
        },
      ]}
    />
  );
};

const useHasLoadedForSeconds = (seconds: number) => {
  const [
    hasLoadedLongerThanSuppliedTimeout,
    setHasLoadedLongerThanSuppliedTimeout,
  ] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setHasLoadedLongerThanSuppliedTimeout(true);
    }, seconds * 1000);
    return () => clearTimeout(timeout);
  }, [seconds]);

  return hasLoadedLongerThanSuppliedTimeout;
};

const useRegisterForPushNotifications = () => {
  const { employeeNumber } = useUserProfile();
  useEffect(() => {
    if (!window.ReactNativeWebView) {
      // To support Go working both in Web and Native wrapper
      // only trigger this flow when we are in a Native wrapper
      return;
    }
    // @ts-expect-error PUSHY_TOKEN is injected on window object from native app
    const pushyToken: string | undefined = window.PUSHY_TOKEN;
    if (!pushyToken) {
      console.warn("No  Pushy token found, skipping registration.");
      return;
    }
    notificationApi
      .put(`/device`, {
        json: { employeeId: employeeNumber, registrationToken: pushyToken },
      })
      .catch((error: unknown) => Sentry.captureException(error));
  }, [employeeNumber]);
};

const IntercomProvider = ({ children }: { children: ReactNode }) => {
  const { givenName, employeeNumber, organization } = useUserProfile();
  const userId = `${ENVIRONMENT === "local" ? "dev" : ENVIRONMENT}-${organization}-${employeeNumber}`;
  const {
    data: token,
    isPending,
    isError,
  } = useIntercomToken({
    userId,
    getIntercomToken: () =>
      logisticsApi.post(`/ambulating/intercom/token`).json(),
  });

  return (
    <BaseIntercomProvider
      appId={INTERCOM_APP_ID}
      name={givenName}
      userId={userId}
      token={token}
      isPending={isPending}
      isError={isError}
    >
      {children}
    </BaseIntercomProvider>
  );
};

const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const getJwt = useCallback(
    () =>
      userManager.getUser().then((user) =>
        // Is this why we sometimes see that chat could not start?
        getEmployeeJwt(z.string().parse(user?.profile.employee_number)),
      ),
    [],
  );

  const matrixClient = useMatrixClient(applicationSchema.enum.Go, getJwt);
  const loadedForALongTime = useHasLoadedForSeconds(3);

  // Use debug chat
  useEffect(() => {
    if (matrixClient === null) {
      return;
    }

    if (import.meta.env.VITE_DEBUG_CHAT) {
      matrixClient.on(ClientEvent.Event, console.log);

      return () => {
        matrixClient.off(ClientEvent.Event, console.log);
      };
    }
  }, [matrixClient]);

  const unreadNotifications = useUnreadNotifications(matrixClient);
  const chatIsFullyLoaded = unreadNotifications !== undefined;

  return (
    <ChatContext.Provider value={{ matrixClient, unreadNotifications }}>
      <LoadingOverlay show={!chatIsFullyLoaded && !loadedForALongTime}>
        {children}
      </LoadingOverlay>
    </ChatContext.Provider>
  );
};

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}
const useNotifyWebAppIsReady = () => {
  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("app:ready");
    }
  }, []);
};

const AmbulatingContainer = () => {
  useGoLogisticsSSEHandlers();
  useGoPatientSSEHandlers();
  useAppVisibilityBreadcrumbs();
  useRegisterForPushNotifications();
  useNotifyWebAppIsReady();

  return (
    <IntercomProvider>
      <ChatProvider>
        <div
          className={clsx([
            styles.ambulatingContainer,
            styles.componentVariableOverrides,
          ])}
        >
          <Header />
          <main className={styles.main}>
            {/* // To support Go working both in Web and Native wrapper
            // only trigger this flow when we are NOT in a Native wrapper */}
            {!window.ReactNativeWebView ? (
              <section className={styles.banners}>
                <NotificationPermitter />
              </section>
            ) : null}
            <Helmet>
              <body className={styles.body} />
            </Helmet>
            <Outlet />
          </main>
        </div>
      </ChatProvider>
    </IntercomProvider>
  );
};

export default AmbulatingContainer;
